import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import Container from '../../../Common/Container';
import Grid from '../../../Common/Grid';
import Button from '../../../Common/Button';
import Header from '../../../../containers/User/HeaderContainer';
import { AuthContext } from '../../../../common/authContext';
import theme from '../../../../common/theme';
import BackToAdminPanel from '../BackToAdminPanel/BackToAdminPanel';
import messages from './messages';
import { Account } from '../../../../models';
import { Partner } from '../../../../models/partner.model';
import { getPartners } from '../../../../services/partners.service';
import {
  deleteAccount,
  getAccounts,
  setAccountActive,
  updateAccountPartners
} from '../../../../services/account.service';

export default function RealtorsScreen() {
  const history = useHistory();
  const intl = useIntl();
  const { authUser } = useContext(AuthContext);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedPartnerIds, setSelectedPartnerIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!authUser?.isSuperAdmin) {
    history.push('/');
  }

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [accountsResponse, partnersResponse] = await Promise.all([
        getAccounts(),
        getPartners()
      ]);
      setAccounts(accountsResponse ?? []);
      setPartners(partnersResponse ?? []);
    } catch (e) {
      setError(String(e || 'Failed to load data'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getRawAccountName = (account: Account) =>
    [account.firstName, account.lastName].filter(Boolean).join(' ');

  const getAccountName = (account: Account) =>
    getRawAccountName(account) || '-';

  const filteredAccounts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return accounts;
    }
    return accounts.filter(account =>
      [
        getRawAccountName(account),
        account.email,
        ...(account.partners ?? []).map(partner => partner.name)
      ].some(value => (value || '').toLowerCase().includes(term))
    );
  }, [accounts, search]);

  const openDialog = (account: Account) => {
    setSelectedAccountId(account._id);
    setSelectedPartnerIds((account.partners ?? []).map(partner => partner._id));
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedAccountId('');
    setSelectedPartnerIds([]);
  };

  const handleTogglePartner = (partnerId: string, checked: boolean) => {
    setSelectedPartnerIds(prev => {
      if (checked) {
        return prev.includes(partnerId) ? prev : [...prev, partnerId];
      }

      return prev.filter(id => id !== partnerId);
    });
  };

  const handleUpdatePartners = async () => {
    if (!selectedAccountId) {
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await updateAccountPartners(selectedAccountId, selectedPartnerIds);
      closeDialog();
      await loadData();
    } catch (e) {
      setError(String(e || 'Failed to update partners'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetActive = async (account: Account, active: boolean) => {
    const name = getRawAccountName(account) || account.email;
    const confirmMessage = active
      ? account.status === 'trial' && messages.confirmActivateTrial
      : messages.confirmDeactivate;
    if (
      confirmMessage &&
      !window.confirm(intl.formatMessage(confirmMessage, { name }))
    ) {
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await setAccountActive(account._id, active);
      await loadData();
    } catch (e) {
      setError(String(e || 'Failed to update account status'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAccount = async (account: Account) => {
    const name = getRawAccountName(account) || account.email;
    if (!window.confirm(intl.formatMessage(messages.confirmDelete, { name }))) {
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await deleteAccount(account._id);
      await loadData();
    } catch (e) {
      setError(String(e || 'Failed to delete account'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <Container
        maxWidth="xl"
        component="div"
        disableGutters
        style={{ padding: theme.spacing(4) }}
      >
        <BackToAdminPanel />
        <Grid container direction="column">
          <Typography variant="h3">
            <FormattedMessage {...messages.realtors} />
          </Typography>

          {error && (
            <Box marginTop={2}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box marginTop={2}>
            <TextField
              label={intl.formatMessage(messages.search)}
              value={search}
              onChange={event => setSearch(event.target.value)}
              variant="outlined"
              size="small"
              fullWidth
            />
          </Box>

          <Box marginTop={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="12%">
                      <FormattedMessage {...messages.name} />
                    </TableCell>
                    <TableCell width="18%">
                      <FormattedMessage {...messages.email} />
                    </TableCell>
                    <TableCell width="18%">
                      <FormattedMessage {...messages.partners} />
                    </TableCell>
                    <TableCell width="8%">
                      <FormattedMessage {...messages.status} />
                    </TableCell>
                    <TableCell width="10%">
                      <FormattedMessage {...messages.signUpDate} />
                    </TableCell>
                    <TableCell width="10%">
                      <FormattedMessage {...messages.trialEnds} />
                    </TableCell>
                    <TableCell width="24%">
                      <Box display="flex" justifyContent="flex-end">
                        <FormattedMessage {...messages.actions} />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAccounts.map(account => (
                    <TableRow key={account._id}>
                      <TableCell>{getAccountName(account)}</TableCell>
                      <TableCell>{account.email}</TableCell>
                      <TableCell>
                        {(account.partners ?? [])
                          .map(partner => partner.name)
                          .join(', ') || '-'}
                      </TableCell>
                      <TableCell>{account.status || '-'}</TableCell>
                      <TableCell>
                        {account.emailVerifiedAt ? (
                          <FormattedDate value={account.emailVerifiedAt} />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {account.trialEndsAt ? (
                          <FormattedDate value={account.trialEndsAt} />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          flexWrap="wrap"
                          style={{ gap: theme.spacing(1) }}
                        >
                          <Button
                            size="small"
                            color="default"
                            variant="outlined"
                            onClick={() => openDialog(account)}
                            disabled={isSubmitting || isLoading}
                          >
                            <FormattedMessage {...messages.updatePartners} />
                          </Button>
                          {account.status !== 'active' && (
                            <Button
                              size="small"
                              color="primary"
                              variant="outlined"
                              onClick={() => handleSetActive(account, true)}
                              disabled={isSubmitting || isLoading}
                            >
                              <FormattedMessage {...messages.activate} />
                            </Button>
                          )}
                          {account.status !== 'inactive' && (
                            <Button
                              size="small"
                              color="default"
                              variant="outlined"
                              onClick={() => handleSetActive(account, false)}
                              disabled={isSubmitting || isLoading}
                            >
                              <FormattedMessage {...messages.deactivate} />
                            </Button>
                          )}
                          <Button
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={() => handleDeleteAccount(account)}
                            disabled={isSubmitting || isLoading}
                          >
                            <FormattedMessage {...messages.delete} />
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!isLoading && filteredAccounts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <FormattedMessage {...messages.noRealtors} />
                      </TableCell>
                    </TableRow>
                  )}

                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <FormattedMessage {...messages.loading} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>
      </Container>

      <Dialog open={isDialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <FormattedMessage {...messages.updatePartners} />
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            marginTop="8px"
            style={{ gap: theme.spacing(1) }}
          >
            {partners.map(partner => (
              <FormControlLabel
                key={partner._id}
                control={
                  <Checkbox
                    checked={selectedPartnerIds.includes(partner._id)}
                    onChange={event =>
                      handleTogglePartner(partner._id, event.target.checked)
                    }
                    color="primary"
                  />
                }
                label={partner.name}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleUpdatePartners}
            disabled={isSubmitting}
          >
            <FormattedMessage {...messages.save} />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
